import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { navigationItems, companyInfo } from '../data/mock';
import logo from '../assets/images/logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl border-b border-gray-100' 
          : 'bg-white/90 backdrop-blur-lg shadow-xl'
      }`}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 lg:py-6">
          
          {/* Logo Section */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            className="relative z-10"
          >
            <Link to="/" className="flex items-center space-x-4 group">
              <div className="relative">
                <motion.div 
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-[0_20px_50px_rgba(29,82,109,0.3)] transition-all duration-500 bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 group-hover:border-[#1d526d]/20"
                  whileHover={{ 
                    rotate: [0, -3, 3, 0],
                    y: -2
                  }}
                  transition={{ duration: 0.6 }}
                >
                  <img 
                    src={logo} 
                    alt="Lepus Group Inc - Freight and Transportation" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </motion.div>
                <motion.div 
                  className="absolute -bottom-2 -right-2 w-6 h-6 bg-[#1d526d] rounded-full border-4 border-white shadow-lg"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>

              <div className="flex flex-col">
                <motion.span 
                  className="text-2xl sm:text-3xl lg:text-4xl font-black text-black group-hover:text-[#1d526d] transition-colors duration-500 leading-none tracking-tight"
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.3 }}
                >
                  {companyInfo.name}
                </motion.span>
                <motion.span 
                  className="text-xs sm:text-sm lg:text-base text-[#1d526d] font-bold group-hover:text-black transition-colors duration-500 tracking-wider mt-1"
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  FREIGHT & TRANSPORTATION
                </motion.span>
              </div>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
              >
                <Link
                  to={item.path}
                  className={`relative px-6 py-4 font-bold text-lg transition-all duration-500 group rounded-xl ${
                    location.pathname === item.path 
                      ? 'text-[#1d526d] bg-gray-50' 
                      : 'text-black hover:text-[#1d526d] hover:bg-gray-50'
                  }`}
                >
                  <motion.span
                    className="relative z-10"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    {item.name}
                  </motion.span>
                  
                  {/* Active indicator */}
                  {location.pathname === item.path && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-full"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.5 }}
                    />
                  )}
                  
                  {/* Hover effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#1d526d]/5 to-[#2a6b89]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    whileHover={{ scale: 1.02 }}
                  />
                </Link>
              </motion.div>
            ))}
            
            {/* Phone Number */}
            {/* Phone Number Block */}
<motion.div
  initial={{ opacity: 0, x: 30 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6, delay: 0.5 }}
  className="hidden xl:flex items-center space-x-3 ml-8 px-4 py-3 bg-black rounded-xl hover:bg-[#1d526d] transition-all duration-500"
>
  <Phone className="w-5 h-5 text-white transition-transform duration-300" />
  <a 
    href={`tel:${companyInfo.phoneRaw}`} 
    className="text-sm font-bold text-white tracking-wider"
  >
    {companyInfo.phone}
  </a>
</motion.div>

{/* Get Quote Button */}
<motion.div
  initial={{ opacity: 0, x: 30 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6, delay: 0.6 }}
  className="ml-6"
>
  <Link
    to="/contact"
    className="overflow-hidden bg-[#1d526d] hover:bg-black text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-500 shadow-xl hover:shadow-2xl"
  >
    <motion.span
      className="relative z-10"
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
    >
      GET QUOTE
    </motion.span>
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-black to-[#1d526d] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      whileHover={{ x: ["-100%", "0%"] }}
      transition={{ duration: 0.5 }}
    />
  </Link>
</motion.div>

          </nav>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="lg:hidden p-4 rounded-xl hover:bg-gray-100 transition-all duration-300 relative z-50 group"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <motion.div
              animate={isMenuOpen ? { rotate: 180 } : { rotate: 0 }}
              transition={{ duration: 0.3 }}
            >
              {isMenuOpen ? 
                <X className="w-7 h-7 text-black group-hover:text-[#1d526d]" /> : 
                <Menu className="w-7 h-7 text-black group-hover:text-[#1d526d]" />
              }
            </motion.div>
          </motion.button>

          {/* Mobile Navigation */}
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={
              isMenuOpen 
                ? { opacity: 1, height: 'auto' } 
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className={`absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-t-2 border-gray-100 shadow-2xl lg:hidden overflow-hidden ${
              isMenuOpen ? 'block' : 'hidden'
            }`}
          >
            <div className="px-6 py-8 space-y-6">
              {navigationItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link
                    to={item.path}
                    className={`block py-5 px-8 rounded-xl font-bold text-lg transition-all duration-300 ${
                      location.pathname === item.path
                        ? 'bg-[#1d526d] text-white shadow-lg'
                        : 'text-black hover:bg-gray-100 hover:text-[#1d526d]'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Contact Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isMenuOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="pt-6 border-t-2 border-gray-100 space-y-4"
              >
                <a 
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="flex items-center space-x-4 px-8 py-4 bg-black text-white rounded-xl hover:bg-[#1d526d] transition-all duration-300 group"
                >
                  <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="font-bold text-lg">{companyInfo.phone}</span>
                </a>
                
                <Link
                  to="/contact"
                  className="block w-full bg-[#1d526d] hover:bg-black text-white px-8 py-5 rounded-xl font-bold text-lg text-center transition-all duration-300 shadow-xl"
                  onClick={() => setIsMenuOpen(false)}
                >
                  GET FREE QUOTE
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;